import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Typography, IconButton, alpha } from '@mui/material';
import { Close, ErrorOutline, CheckCircleOutline, InfoOutlined, WarningAmber } from '@mui/icons-material';
import { palette } from '../../theme';

const ToastContext = createContext(null);

const TONES = {
  success: { icon: CheckCircleOutline, color: palette.success },
  error: { icon: ErrorOutline, color: palette.danger },
  warning: { icon: WarningAmber, color: palette.warning },
  info: { icon: InfoOutlined, color: palette.primary },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (message, tone = 'info', title) => {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setToasts((current) => [...current.slice(-3), { id, message, tone, title }]);
      setTimeout(() => dismiss(id), tone === 'error' ? 6500 : 4200);
    },
    [dismiss]
  );

  // Surface session interruptions raised by the API client.
  useEffect(() => {
    const handler = (event) => push(event.detail, 'warning', 'Session expired');
    window.addEventListener('bluenode:session-expired', handler);
    return () => window.removeEventListener('bluenode:session-expired', handler);
  }, [push]);

  const value = useMemo(
    () => ({
      notify: push,
      success: (message, title) => push(message, 'success', title),
      error: (message, title) => push(message, 'error', title),
      warning: (message, title) => push(message, 'warning', title),
      info: (message, title) => push(message, 'info', title),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 12, md: 24 },
          bottom: { xs: 92, md: 24 },
          zIndex: 2200,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => {
          const tone = TONES[toast.tone] || TONES.info;
          const Icon = tone.icon;
          return (
            <Box
              key={toast.id}
              role="status"
              sx={{
                pointerEvents: 'auto',
                minWidth: { xs: 260, sm: 330 },
                maxWidth: 420,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                px: 2,
                py: 1.5,
                borderRadius: 3,
                border: `1px solid ${alpha(tone.color, 0.32)}`,
                background: `linear-gradient(150deg, ${alpha('#0B1224', 0.96)}, ${alpha('#050816', 0.96)})`,
                backdropFilter: 'blur(18px)',
                boxShadow: `0 22px 50px -22px rgba(0,0,0,0.95), 0 0 0 1px ${alpha(tone.color, 0.08)}`,
                animation: 'holoFloat 320ms cubic-bezier(0.2, 0.9, 0.3, 1)',
              }}
            >
              <Icon sx={{ color: tone.color, fontSize: 20, mt: 0.15 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {toast.title && (
                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: tone.color, mb: 0.25 }}>
                    {toast.title}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary', lineHeight: 1.45 }}>
                  {toast.message}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => dismiss(toast.id)} sx={{ color: 'text.secondary', mt: -0.5 }}>
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>
    </ToastContext.Provider>
  );
}

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
};
